import type { TimeUnit } from '@/utils/calculatorMath'
import CalculatorActions from '@/components/calculators/CalculatorActions'
import ValidationAlert from '@/components/calculators/ValidationAlert'
import { calculateDoublingTime, formatNumber } from '@/utils/calculatorMath'
import {Lightbulb} from 'lucide-react'
import { useState } from 'react'

const TIME_UNITS: TimeUnit[] = ['Hours', 'Minutes', 'Days']

interface Result {
  doublingTime: number
  growthRate: number
  timeUnit: string
}

function CellDoublingTimeCalculator() {
  const [n0, setN0] = useState('')
  const [nt, setNt] = useState('')
  const [time, setTime] = useState('')
  const [timeUnit, setTimeUnit] = useState<TimeUnit>('Hours')
  const [result, setResult] = useState<Result | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const clearFeedback = () => {
    setCopied(false)
  }

  const handleReset = () => {
    setN0('')
    setNt('')
    setTime('')
    setTimeUnit('Hours')
    setResult(null)
    setError(null)
    setCopied(false)
  }

  const handleCalculate = () => {
    clearFeedback()
    const n0Val = Number(n0)
    const ntVal = Number(nt)
    const tVal = Number(time)

    if (n0.trim() === '' || nt.trim() === '' || time.trim() === '') {
      setError('Please fill in all three fields: N0, Nt, and elapsed time.')
      setResult(null)
      return
    }

    if ([n0Val, ntVal, tVal].some(v => Number.isNaN(v) || v < 0)) {
      setError('Please enter valid, non-negative numbers only.')
      setResult(null)
      return
    }

    if (n0Val === 0 || tVal === 0) {
      setError('Initial concentration (N0) and elapsed time must be greater than zero.')
      setResult(null)
      return
    }

    if (ntVal === n0Val) {
      setError('Final concentration (Nt) must differ from initial concentration (N0) to calculate growth.')
      setResult(null)
      return
    }

    const { doublingTime, growthRate } = calculateDoublingTime(n0Val, ntVal, tVal)

    if (!Number.isFinite(doublingTime) || !Number.isFinite(growthRate)) {
      setError('Calculation produced an invalid result. Please verify your inputs.')
      setResult(null)
      return
    }

    setError(null)
    setResult({ doublingTime, growthRate, timeUnit })
  }

  const resultText = result
    ? `Doubling Time (Td) = ${formatNumber(result.doublingTime)} ${result.timeUnit}; Growth Rate (µ) = ${formatNumber(result.growthRate)} per ${result.timeUnit.replace(/s$/, '').toLowerCase()}`
    : ''

  const handleCopy = async () => {
    if (!resultText)
      return
    try {
      await navigator.clipboard.writeText(resultText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
    catch {
      setError('Unable to copy to clipboard.')
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-1">Cell Doubling Time Calculator</h2>
      <p className="text-sm text-slate-600 mb-4">
        Calculate doubling time (Td) and exponential growth rate (µ) from initial and final cell counts.
      </p>

      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-4 mb-6">
        <Lightbulb className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-slate-700 leading-relaxed">
          <span className="font-semibold text-slate-900">How to use this calculator: </span>
          Enter your starting cell concentration (N0), your final cell concentration (Nt) measured after a period of growth, and the elapsed time between the two counts. Pick the correct time unit, then press <span className="font-semibold">Calculate</span> to instantly derive the doubling time and exponential growth rate.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="n0" className="block text-sm font-semibold text-slate-800 mb-1.5">
            Initial Cell Concentration (N0)
          </label>
          <input
            id="n0"
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            placeholder="e.g. 100000"
            value={n0}
            onChange={e => { setN0(e.target.value); clearFeedback() }}
            aria-describedby="n0-hint"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
          />
          <p id="n0-hint" className="mt-1.5 text-xs text-slate-500 leading-snug">
            The cell count or concentration you measured at the start of the growth period (time zero).
          </p>
        </div>
        <div>
          <label htmlFor="nt" className="block text-sm font-semibold text-slate-800 mb-1.5">
            Final Cell Concentration (Nt)
          </label>
          <input
            id="nt"
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            placeholder="e.g. 400000"
            value={nt}
            onChange={e => { setNt(e.target.value); clearFeedback() }}
            aria-describedby="nt-hint"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
          />
          <p id="nt-hint" className="mt-1.5 text-xs text-slate-500 leading-snug">
            The cell count or concentration measured again after the elapsed time has passed. Must differ from N0.
          </p>
        </div>
        <div>
          <label htmlFor="elapsed-time" className="block text-sm font-semibold text-slate-800 mb-1.5">
            Elapsed Time (t)
          </label>
          <div className="flex gap-2">
            <input
              id="elapsed-time"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              placeholder="e.g. 24"
              value={time}
              onChange={e => { setTime(e.target.value); clearFeedback() }}
              aria-describedby="elapsed-time-hint"
              className="flex-1 min-w-0 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
            />
            <select
              aria-label="Elapsed time unit"
              value={timeUnit}
              onChange={e => { setTimeUnit(e.target.value as TimeUnit); clearFeedback() }}
              className="rounded-md border border-slate-300 px-2 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600"
            >
              {TIME_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <p id="elapsed-time-hint" className="mt-1.5 text-xs text-slate-500 leading-snug">
            The total time between measuring N0 and Nt. Choose Hours, Minutes, or Days to match your experiment.
          </p>
        </div>
      </div>

      <ValidationAlert message={error} />

      <div className="flex flex-wrap gap-3 mt-4 mb-4">
        <button
          type="button"
          onClick={handleCalculate}
          className="px-5 py-2.5 rounded-md text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 transition-colors"
        >
          Calculate
        </button>
      </div>

      <div
        role="status"
        aria-live="polite"
        className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 mb-4 min-h-[76px] flex flex-col justify-center gap-2"
      >
        {result
          ? (
            <>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Doubling Time (Td)</p>
                <p className="text-2xl font-bold text-slate-900">{formatNumber(result.doublingTime)} {result.timeUnit}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Growth Rate (µ)</p>
                <p className="text-2xl font-bold text-slate-900">{formatNumber(result.growthRate)} / {result.timeUnit.replace(/s$/, '').toLowerCase()}</p>
              </div>
            </>
          )
          : (
            <p className="text-sm text-slate-500">Your calculated doubling time and growth rate will appear here.</p>
          )}
      </div>

      <CalculatorActions onReset={handleReset} onCopy={handleCopy} copied={copied} />

      <div className="mt-8 border-t border-slate-200 pt-6">
        <h3 className="text-lg font-bold text-slate-900 mb-2">The Mathematics Behind Doubling Time</h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-3">
          Exponential cell growth follows Nt = N0 × e^(µt), where µ is the specific growth rate. Taking the natural logarithm of both sides gives ln(Nt / N0) = µt, so µ = ln(Nt / N0) / t. Doubling time is the time required for the population to reach 2 × N0, so substituting Nt = 2N0 gives ln(2) = µ × Td, which rearranges to Td = ln(2) / µ, or equivalently Td = (t × ln2) / ln(Nt / N0).
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          A shorter doubling time indicates faster proliferation, which is why this metric is widely used to characterize cancer cell lines, monitor bioreactor cultures, and compare the effects of growth media or drug treatments on cell populations.
        </p>

        <h4 className="text-base font-bold text-slate-900 mt-6 mb-3">Worked Examples</h4>
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-1">Example 1 — Fast-growing bacterial culture</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              N0 = 1,000 cells/mL, Nt = 8,000 cells/mL, measured after t = 3 Hours.
              <br />
              µ = ln(8000 / 1000) / 3 = ln(8) / 3 = 2.079 / 3 = <span className="font-semibold text-slate-900">0.693 per hour</span>.
              <br />
              Td = (3 × ln2) / ln(8) = (3 × 0.693) / 2.079 = <span className="font-semibold text-slate-900">1 hour</span>. This culture doubles every hour.
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-1">Example 2 — Mammalian cell line in culture</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              N0 = 200,000 cells, Nt = 800,000 cells, measured after t = 48 Hours.
              <br />
              µ = ln(800000 / 200000) / 48 = ln(4) / 48 = 1.386 / 48 = <span className="font-semibold text-slate-900">0.0289 per hour</span>.
              <br />
              Td = (48 × ln2) / ln(4) = (48 × 0.693) / 1.386 = <span className="font-semibold text-slate-900">24 hours</span>. This is a typical doubling time for many adherent mammalian cell lines.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CellDoublingTimeCalculator
